import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Lock, KeyRound, CheckCircle2, AlertCircle } from 'lucide-react';
import { API_URL } from '../config';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      toast.error('Invalid reset link');
      setTimeout(() => navigate('/'), 2000);
    }
  }, [searchParams, navigate]);

  const validatePassword = () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      setSuccess(true);
      toast.success(response.data.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#050505' }}>
        <div className="w-full max-w-md text-center">
          <div 
            className="p-8 rounded-2xl"
            style={{
              background: '#121212',
              border: '1px solid rgba(238, 28, 78, 0.3)'
            }}
          >
            <div className="flex justify-center mb-6">
              <div 
                className="p-4 rounded-full"
                style={{ background: 'rgba(16, 185, 129, 0.1)' }}
              >
                <CheckCircle2 className="w-16 h-16" style={{ color: '#10B981' }} />
              </div>
            </div>
            
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#EE1C4E' }}>
              Password Reset Successful!
            </h2>
            <p className="text-gray-400 mb-6">
              Your password has been updated successfully.
            </p>
            <p className="text-gray-500 text-sm">
              Redirecting to login page...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-black mb-2" style={{ color: '#EE1C4E' }}>
            RedChannel
          </h1>
          <p className="text-gray-400 text-sm">Reset Your Password</p>
        </div>

        {/* Reset Password Form */}
        <div
          className="p-6 rounded-2xl"
          style={{
            background: '#121212',
            border: '1px solid #2A2A2A'
          }}
          data-testid="reset-password-form"
        >
          <div className="mb-6">
            <div 
              className="p-3 rounded-lg mb-4"
              style={{ background: 'rgba(238, 28, 78, 0.1)', border: '1px solid rgba(238, 28, 78, 0.3)' }}
            >
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#EE1C4E' }} />
                <div className="text-sm">
                  <p className="text-gray-300 mb-1">
                    <strong>Important:</strong>
                  </p>
                  <ul className="text-gray-400 space-y-1 list-disc list-inside">
                    <li>This link expires in 1 hour</li>
                    <li>This link can only be used once</li>
                    <li>Choose a strong password</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="new-password" className="text-gray-300 mb-2 block">
                New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-11 bg-black/40 border-gray-700 text-white placeholder:text-gray-600 focus:border-[#EE1C4E] h-12"
                  data-testid="new-password-input"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">At least 6 characters</p>
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-gray-300 mb-2 block">
                Confirm New Password
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-11 bg-black/40 border-gray-700 text-white placeholder:text-gray-600 focus:border-[#EE1C4E] h-12"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-white font-bold rounded-full transition-all hover:scale-[1.02]"
              style={{
                background: '#EE1C4E',
                boxShadow: '0 4px 14px rgba(238, 28, 78, 0.4)'
              }}
              data-testid="reset-submit-button"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-[#EE1C4E] transition-colors text-sm"
              data-testid="back-to-login-link"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
